/**
 * The Telegram launch signal, read synchronously — no SDK required.
 *
 * ## Why this exists (the race it closes)
 *
 * `detectLocale()` prefers the Telegram user's `language_code` over `navigator.language`
 * because inside the WebView `navigator.language` is the PHONE's language, not the user's
 * (Dmitry's 2026-07-17 screenshot of EN chrome over RU replies). But mini-app resolves the
 * locale at module-evaluation time, and the SDK `<script>` is `async` with
 * `bootstrapTelegram()` deliberately not awaited (third-party CDN latency must not sit in
 * front of first paint) — so whenever the SDK has not landed yet, the global is absent and
 * the resolver fell through to the phone's language. Intermittent, racing a CDN.
 *
 * ⛔ The value is NOT late. Read from `https://telegram.org/js/telegram-web-app.js` — the
 * exact file `apps/mini-app/index.html` loads — `initDataUnsafe` is not fetched, it is
 * `location.hash`, parsed: the SDK snapshots `location.hash`, url-parses `tgWebAppData`
 * out of it, and url-parses THAT into `initDataUnsafe` (SDK lines 7–19, 65–101, 298+).
 * No network, no handshake. Telegram puts the bytes in the URL before any script runs.
 *
 * ## Faithful to the SDK, or more fragile than the bug it replaces
 *
 * This reader mirrors the SDK's own derivation function-by-function (`urlParseHashParams`,
 * `urlParseQueryString`, `urlSafeDecode` — including the SDK's truncating
 * `param.split('=')[1]` value read). Where they disagree, `initDataUnsafe` and this reader
 * would resolve different users from the same launch, which is a divergence nothing tests.
 * The one deliberate deviation: **read-only** — the SDK WRITES its merged snapshot back to
 * `sessionStorage['__telegram__initParams']`; this reader only READS it. A naive writer
 * running with an empty hash (standalone `pnpm dev`, or any future router-shaped hash)
 * would erase the SDK's real snapshot with an empty set — reintroducing this exact bug in
 * a narrower window, the worst possible outcome. The SDK owns snapshot writes.
 *
 * The snapshot fallback is load-bearing, not paranoia: the hash does not survive an
 * in-app navigation, so a reload mid-app resolves from the SDK's snapshot or not at all.
 *
 * ## Trust posture: unchanged
 *
 * `detectLocale()` already reads `initDataUnsafe` — the UNVERIFIED view; the signed
 * `initData` is a different field. This reads the same bytes from where Telegram itself
 * puts them. No new trust assumption: it selects a dictionary, it authorizes nothing.
 */
function decode(raw) {
    try {
        return decodeURIComponent(raw.replace(/\+/g, '%20'));
    }
    catch {
        return raw;
    }
}
/** Mirrors the SDK's `urlParseQueryString`, including its truncating `split('=')` value read. */
function qs(s) {
    const out = {};
    for (const pair of s.split('&')) {
        const [k, v] = pair.split('=');
        out[decode(k)] = v === undefined ? null : decode(v);
    }
    return out;
}
/**
 * Mirrors the SDK's `urlParseHashParams` branching: a bare path fragment carries no launch
 * data (`_path` in the SDK, nothing here); a `?` splits path from query; no `?` but an `=`
 * means the whole fragment is the query string.
 */
export function telegramLaunchUserLanguage() {
    try {
        const hash = window.location.hash.replace(/^#/, '');
        let params = {};
        if (hash.includes('=') || hash.includes('?')) {
            const q = hash.indexOf('?');
            params = qs(q >= 0 ? hash.slice(q + 1) : hash);
        }
        let data = params.tgWebAppData;
        if (typeof data !== 'string') {
            // Hash lost (in-app navigation, redirect) — the SDK's own snapshot is the fallback
            // for exactly the key the hash is missing, the way the SDK merges storedParams.
            try {
                const raw = window.sessionStorage.getItem('__telegram__initParams');
                const snap = raw ? JSON.parse(raw) : null;
                if (snap && typeof snap.tgWebAppData === 'string')
                    data = snap.tgWebAppData;
            }
            catch {
                /* fall through */
            }
        }
        if (typeof data !== 'string' || !data.length)
            return null;
        // `initDataUnsafe` = the query string inside tgWebAppData, decoded once more; the SDK
        // JSON-parses values that brace-balance, and `user` is one of those.
        const user = qs(data).user;
        if (typeof user !== 'string' || !user.startsWith('{') || !user.endsWith('}'))
            return null;
        const lang = JSON.parse(user).language_code;
        return typeof lang === 'string' && lang ? lang : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=telegramLaunchLocale.js.map